import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Branch } from '@/queries/useBranches'
import { useTranslation } from 'react-i18next'

interface DeleteBranchDialogProps {
  branch: Branch | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void | Promise<void>
  isLoading?: boolean
  error?: Error | null
}

export default function DeleteBranchDialog({
  branch,
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
  error,
}: DeleteBranchDialogProps) {
  const { t } = useTranslation('admin')
  
  const handleConfirm = async () => {
    await onConfirm()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-branch-dialog">
        <DialogHeader>
          <DialogTitle data-testid="delete-branch-dialog-title">
            {t('branches.dialog.deleteTitle')}
          </DialogTitle>
          <DialogDescription 
            data-testid="delete-branch-dialog-description"
            dangerouslySetInnerHTML={{
              __html: t('branches.dialog.deleteDescription', { name: branch?.name || '' })
            }}
          />
        </DialogHeader>

        {error && (
          <Alert variant="destructive" data-testid="delete-branch-dialog-error">
            <AlertDescription>
              {error.message || t('branches.dialog.deleteError')}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            data-testid="delete-branch-dialog-cancel-button"
          >
            {t('branches.form.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isLoading}
            data-testid="delete-branch-dialog-confirm-button"
          >
            {isLoading ? t('common:buttons.deleting', { defaultValue: 'Deleting...' }) : t('common:buttons.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

